export default function createPoller({ fetchFn, interval, immediate = false, maxBackoff = 5, backoffFactor = 2, onError = null }) {
  let timerId = null;
  let currentDelay = interval;
  let stopped = true;
  let destroyed = false;
  let loading = false;
  let handleVisibilityChange = null;

  async function tick() {
    if (stopped || destroyed) return;

    loading = true;
    try {
      await fetchFn();
      currentDelay = interval;
    } catch (error) {
      currentDelay = Math.min(currentDelay * backoffFactor, interval * maxBackoff);
      if (onError) {
        onError(error);
      }
    } finally {
      loading = false;
    }

    scheduleNext();
  }

  function scheduleNext() {
    if (stopped || destroyed) return;
    timerId = setTimeout(tick, currentDelay);
  }

  function clearTimer() {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  }

  function start() {
    if (destroyed) return;
    stopped = false;
    clearTimer();

    if (immediate) {
      tick();
    } else {
      scheduleNext();
    }
  }

  function stop() {
    stopped = true;
    clearTimer();
  }

  function destroy() {
    stop();
    destroyed = true;
    if (handleVisibilityChange) {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      handleVisibilityChange = null;
    }
  }

  function restart() {
    stop();
    currentDelay = interval;
    stopped = false;
    tick();
  }

  // Visibility API: pause when tab is hidden, resume on focus
  handleVisibilityChange = function () {
    if (destroyed) return;
    if (document.hidden) {
      clearTimer();
    } else if (!stopped) {
      clearTimer();
      tick();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return {
    get isPolling() { return !stopped; },
    get isLoading() { return loading; },
    start,
    stop,
    destroy,
    restart,
  };
}
